import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, ChevronRight, RefreshCw, Clock, AlertTriangle, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Phase 41 (2026-07-28) — Read-only viewer for the auto-heal event log.
 * Every 15-minute cron and every manual `/admin/auto-heal/run` produces one row.
 */

function fmtTime(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(); } catch { return iso; }
}

function StatusPill({ event }) {
  if (event.aborted) {
    return <span className="rounded-full px-2 py-0.5 text-[11px] font-medium bg-red-50 text-red-700" data-testid="event-pill-aborted">Aborted</span>;
  }
  if (event.repairs_count > 0) {
    return <span className="rounded-full px-2 py-0.5 text-[11px] font-medium bg-emerald-50 text-emerald-700" data-testid="event-pill-repaired">Repaired</span>;
  }
  if (event.escalated_count > 0) {
    return <span className="rounded-full px-2 py-0.5 text-[11px] font-medium bg-amber-50 text-amber-700" data-testid="event-pill-escalated">Escalated</span>;
  }
  return <span className="rounded-full px-2 py-0.5 text-[11px] font-medium bg-slate-100 text-slate-600" data-testid="event-pill-clean">Clean</span>;
}

export default function AutoHealLogPage() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [settings, setSettings] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/auto-heal/events?limit=100");
      setEvents(data.events || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load events");
    } finally { setLoading(false); }
  };

  const loadSettings = async () => {
    try {
      const { data } = await api.get("/admin/auto-heal/settings");
      setSettings(data);
    } catch { /* no-op */ }
  };

  useEffect(() => { load(); loadSettings(); }, []);

  const triggerRun = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/admin/auto-heal/run",
        { reason: "Manual admin trigger from /admin/auto-heal-log" });
      if (data.aborted) {
        toast.warning(`Run aborted — ${data.aborted_reason}`);
      } else if (data.repairs_count > 0) {
        toast.success(`Repaired ${data.repairs_count} · Escalated ${data.escalated_count}`);
      } else if (data.escalated_count > 0) {
        toast.warning(`Nothing repaired · ${data.escalated_count} escalated for review`);
      } else {
        toast.success("Clean run — no integrity issues found");
      }
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Trigger failed");
    } finally { setRunning(false); }
  };

  const revert = async (eventId) => {
    if (!window.confirm("Revert this auto-heal run? All reversal/correction JEs it created will be deleted."))
      return;
    try {
      const { data } = await api.post(`/admin/auto-heal/events/${eventId}/revert`);
      toast.success(`Reverted — deleted ${data.deleted_je_count} JE(s)`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Revert failed");
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-4 space-y-4" data-testid="auto-heal-log-page">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <ShieldCheck className="h-6 w-6 text-emerald-600" /> Data Integrity Auto-Heal Log
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            Autonomous cron runs every 15 minutes. Detects and safely repairs data integrity
            issues. Ambiguous items are escalated to the{" "}
            <Link to="/admin/review-queue" className="text-blue-600 hover:underline"
                   data-testid="link-review-queue">
              admin review queue
            </Link>. All repairs post reversing JEs — nothing is deleted.
          </p>
        </div>
        <button
          onClick={triggerRun}
          disabled={running || loading}
          className="inline-flex items-center gap-2 rounded-lg bg-slate-900 text-white text-sm px-3 py-2 disabled:opacity-50"
          data-testid="trigger-run-btn"
        >
          <RefreshCw className={`h-4 w-4 ${running ? "animate-spin" : ""}`} />
          {running ? "Running…" : "Run Now"}
        </button>
      </div>

      {settings && (
        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 flex flex-wrap items-center gap-4 text-xs text-slate-700"
             data-testid="settings-strip">
          <span>
            Auto-Heal:{" "}
            <span className={settings.auto_heal_enabled ? "text-emerald-700 font-semibold" : "text-red-700 font-semibold"}>
              {settings.auto_heal_enabled ? "ENABLED" : "DISABLED"}
            </span>
          </span>
          <span>Cron interval: every {settings.interval_minutes} min</span>
          <span>Global kill-switch: {settings.global_kill_switch_reversals} reversals/run</span>
          <Link to="/settings#auto-heal" className="ml-auto text-blue-600 hover:underline"
                 data-testid="link-admin-settings">
            Configure →
          </Link>
        </div>
      )}

      <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden"
           data-testid="events-table">
        <table className="w-full text-sm">
          <thead className="text-[11px] uppercase tracking-widest text-slate-500 bg-slate-50">
            <tr>
              <th className="text-left px-4 py-2 font-medium">When</th>
              <th className="text-left px-4 py-2 font-medium">Trigger</th>
              <th className="text-right px-4 py-2 font-medium">Findings</th>
              <th className="text-right px-4 py-2 font-medium">Repairs</th>
              <th className="text-right px-4 py-2 font-medium">Escalated</th>
              <th className="text-right px-4 py-2 font-medium">Duration</th>
              <th className="text-center px-4 py-2 font-medium">Status</th>
              <th className="px-2 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {events.length === 0 && !loading && (
              <tr>
                <td colSpan={8} className="text-center py-8 text-slate-500">
                  No auto-heal events yet. Click <b>Run Now</b> to trigger the first run.
                </td>
              </tr>
            )}
            {events.map((ev) => {
              const key = ev.id;
              const isOpen = !!expanded[key];
              return (
                <>
                  <tr
                    key={key}
                    className="border-t border-slate-100 hover:bg-slate-50 cursor-pointer"
                    onClick={() => setExpanded((s) => ({ ...s, [key]: !isOpen }))}
                    data-testid={`event-row-${key}`}
                  >
                    <td className="px-4 py-2 text-slate-700 whitespace-nowrap">
                      <Clock className="h-3.5 w-3.5 inline mr-1 text-slate-400" />
                      {fmtTime(ev.ran_at)}
                    </td>
                    <td className="px-4 py-2 text-slate-600 truncate max-w-[180px]">{ev.triggered_by}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{ev.findings_count}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-emerald-700 font-medium">{ev.repairs_count}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-amber-700 font-medium">{ev.escalated_count}</td>
                    <td className="px-4 py-2 text-right tabular-nums text-slate-500">{ev.duration_ms}ms</td>
                    <td className="px-4 py-2 text-center"><StatusPill event={ev} /></td>
                    <td className="px-2 py-2 text-slate-400">
                      <ChevronRight className={`h-4 w-4 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                    </td>
                  </tr>
                  {isOpen && (
                    <tr key={key + "-detail"} className="border-t border-slate-100 bg-slate-50">
                      <td colSpan={8} className="px-4 py-3">
                        {ev.aborted && (
                          <div className="rounded-md bg-red-50 border border-red-200 p-2 text-xs text-red-700 mb-3 flex items-start gap-2">
                            <AlertTriangle className="h-4 w-4 mt-0.5" />
                            <span><b>Aborted:</b> {ev.aborted_reason}</span>
                          </div>
                        )}
                        {ev.reverted && (
                          <div className="rounded-md bg-slate-100 border border-slate-300 p-2 text-xs text-slate-700 mb-3">
                            <b>Reverted</b> at {fmtTime(ev.reverted_at)} by {ev.reverted_by}
                          </div>
                        )}
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="text-[10px] uppercase tracking-widest text-slate-500">
                              <th className="text-left py-1">Rule</th>
                              <th className="text-right py-1">Findings</th>
                              <th className="text-right py-1">Repairs</th>
                              <th className="text-right py-1">Escalated</th>
                              <th className="text-right py-1">Rev JEs</th>
                              <th className="text-left py-1 pl-4">Detail</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(ev.per_rule || []).map((r, i) => (
                              <tr key={i} className="border-t border-slate-200">
                                <td className="py-1 pr-2 font-mono text-slate-800">{r.rule_id}</td>
                                <td className="py-1 text-right tabular-nums">{r.findings}</td>
                                <td className="py-1 text-right tabular-nums text-emerald-700">{r.repairs}</td>
                                <td className="py-1 text-right tabular-nums text-amber-700">{r.escalations}</td>
                                <td className="py-1 text-right tabular-nums">{r.reversal_jes_posted}</td>
                                <td className="py-1 pl-4 text-slate-500">
                                  {r.error && <span className="text-red-700">ERR: {r.error}</span>}
                                  {r.aborted && <span className="text-red-700">ABORTED: {r.aborted_reason}</span>}
                                  {!r.error && !r.aborted && r.findings === 0 && (
                                    <span className="text-slate-400 italic inline-flex items-center gap-1">
                                      <CheckCircle2 className="h-3 w-3" /> clean
                                    </span>
                                  )}
                                  {!r.error && !r.aborted && r.findings > 0 && (
                                    <details>
                                      <summary className="cursor-pointer text-blue-600 text-[11px]">
                                        {r.findings_detail?.length || 0} finding(s)
                                      </summary>
                                      <pre className="text-[10px] mt-1 bg-white p-2 rounded border overflow-x-auto max-w-[720px]">{JSON.stringify(r.findings_detail, null, 2)}</pre>
                                    </details>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {ev.repairs_count > 0 && !ev.reverted && (
                          <div className="mt-3">
                            <button
                              onClick={(e) => { e.stopPropagation(); revert(ev.id); }}
                              className="text-xs text-red-700 hover:underline"
                              data-testid={`revert-${ev.id}`}
                            >
                              Revert this run
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
